import {
  getGetNodoPortaleQueryKey,
  NodoDto,
  ReminderDto,
  ReminderType,
  Role,
  useUpdateReminders,
} from "@/api";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Table from "@/elements/Table";
import { getReminderTypeHelper } from "@/utils/reminderTypeUtils";
import { useDbRelationsUpdateForm } from "@/utils/useDbRelationsUpdate";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from "@tanstack/react-table";
import { sortBy } from "lodash";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { withOpeningReset } from "../../utils/withOpeningReset";
import { SmallLoading } from "../Loading";
import { portaleConfig } from "@/portaliConfig";
import { roles } from "@/utils/rolesAndPermissions";
import { _rolesNameMap } from "@/utils/rolesAndPermissionsData/rolesNameMap";
import { useTranslation } from "react-i18next";

type WrappedReminderType = { type: ReminderType };

const columnHelper = createColumnHelper<WrappedReminderType>();

function GestisciReminderDialog({
  open = false,
  onClose,
  nodo,
}: {
  open?: boolean;
  onClose?: () => void;
  nodo: NodoDto;
}) {
  const { t, i18n } = useTranslation();

  const handleClose = () => {
    onClose?.();
  };

  const reminders = useMemo(() => {
    if (!nodo.reminders) {
      return null;
    }

    return sortBy(nodo.reminders, "deltaDays");
  }, [nodo]);

  const {
    data,
    control,
    handleSubmit,
    getChangeSubmitData,
    getFieldName,
    formState: { errors },
    create,
    remove,
    restore,
    getEntityState,
  } = useDbRelationsUpdateForm(reminders, { deleteChangeType: "deleted" });

  const wrappedReminderTypes = useMemo(() => {
    const wrappedReminderTypes: WrappedReminderType[] = [];

    if (nodo.haAnalisi) {
      wrappedReminderTypes.push({ type: "analisi" });
    }

    if (nodo.haAlloggiamentiFiltro) {
      wrappedReminderTypes.push({ type: "filtro" });
    }

    if (nodo.haStoricoBonifiche) {
      wrappedReminderTypes.push({ type: "bonifica" });
    }

    return wrappedReminderTypes;
  }, [nodo]);

  const columns = useMemo<
    ColumnDef<(typeof wrappedReminderTypes)[number], any>[]
  >(() => {
    const deltaDaysCell = (role: Role) => {
      return ({
        row: {
          original: { type },
        },
      }: CellContext<(typeof wrappedReminderTypes)[number], any>) => {
        //const data = getChangeSubmitData();
        return (
          <ReminderTableCell
            data={data}
            role={role}
            type={type}
            getEntityState={getEntityState}
            getFieldName={getFieldName}
            control={control}
            remove={remove}
            restore={restore}
            create={create}
          />
        );
      };
    };

    const columns = [
      columnHelper.accessor(
        (r) => getReminderTypeHelper(r.type, i18n.language)?.label,
        {
          id: "reminderType",
          header: t("gestisciReminderDialog.colonne.reminder"),
        }
      ),

      ...roles.map((role) => {
        return columnHelper.display({
          header: _rolesNameMap[role],
          cell: deltaDaysCell(role),
        });
      }),

      // columnHelper.accessor("daysBeforeForAzienda", {
      //   header: "Azienda",
      //   cell: daysBeforeCell("daysBeforeForAzienda"),
      // }),
      // columnHelper.accessor("daysBeforeForTecnici", {
      //   header: "Tecnici",
      //   cell: daysBeforeCell("daysBeforeForTecnici"),
      // }),
      // columnHelper.accessor("daysBeforeForLaboratorio", {
      //   header: "Laboratorio",
      //   cell: daysBeforeCell("daysBeforeForLaboratorio"),
      // }),
      // columnHelper.accessor("daysBeforeForCommerciali", {
      //   header: "Commerciali",
      //   cell: daysBeforeCell("daysBeforeForCommerciali"),
      // }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [control, getFieldName, data, getEntityState, i18n.language, t]);

  const { mutateAsync, isLoading, error } = useUpdateReminders({
    mutation: {
      onSuccess: () => {
        toast.success(t("gestisciReminderDialog.toast.salvate"));
      },
      onError: () => {
        toast.error(t("gestisciReminderDialog.toast.errore"));
      },
    },
  });

  const queryClient = useQueryClient();

  const onSubmit = async () => {
    try {
      await mutateAsync({
        id: nodo.id,
        data: getChangeSubmitData(),
      });
      await queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id),
      });
      onClose?.();
    } catch (err) {}
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle>
        {t("gestisciReminderDialog.titolo", { nome: nodo.nome })}
      </DialogTitle>
      <DialogContent>
        {reminders && (
          <Box>
            {errors.root?.message && (
              <Alert sx={{ mt: 1 }} severity="warning">
                {errors.root?.message}
              </Alert>
            )}

            <Table
              columns={columns}
              data={wrappedReminderTypes}
              sortable={false}
              paginated={false}
              initialSort={[{ id: "reminderType", desc: true }]}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}

        <Button
          size="small"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading}
        >
          {t("azioni.salva")}
        </Button>

        {onClose && (
          <Button size="small" color="light" onClick={handleClose}>
            {t("azioni.chiudi")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(GestisciReminderDialog);

function ReminderTableCell({
  data,
  role,
  type,
  getEntityState,
  getFieldName,
  control,
  remove,
  restore,
  create,
}: {
  data: ReminderDto[];
  role: Role;
  type: ReminderType;
  getEntityState: (index: number) => string;
  getFieldName: (index: number, key: keyof ReminderDto) => string;
  control: any;
  remove: any;
  restore: any;
  create: any;
}) {
  const { t } = useTranslation();
  const _reminders = data.filter(
    (r) => r.role === role && r.reminderType === type
  );
  const indexes = _reminders.map((r) => data.indexOf(r));
  const labels = [
    t("gestisciReminderDialog.labels.primo"),
    t("gestisciReminderDialog.labels.secondo"),
  ];

  return (
    <Box display="flex" flexDirection="row" gap={1}>
      {labels.map((label, _i) => {
        if (indexes.length > _i) {
          const reminderIndex = indexes[_i];
          const isDeleted = getEntityState(reminderIndex) === "deleted";

          return (
            <Controller
              key={label}
              name={getFieldName(reminderIndex, "deltaDays")}
              control={control}
              render={({ field }) => (
                <DaysSelect
                  label={label}
                  value={
                    getEntityState(reminderIndex) === "deleted"
                      ? null
                      : field.value
                  }
                  onChange={(value) => {
                    if (value === null) {
                      remove(reminderIndex);
                    } else {
                      if (isDeleted) {
                        restore(reminderIndex);
                      }
                      field.onChange(value);
                    }
                  }}
                />
              )}
            />
          );
        } else {
          return (
            <DaysSelect
              label={label}
              key={label}
              value={null}
              onChange={(value) => {
                if (value) {
                  create({
                    deltaDays: value,
                    role,
                    reminderType: type,
                  });
                }
              }}
            />
          );
        }
      })}
    </Box>
  );
}

function DaysSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const { t } = useTranslation();

  return (
    <Box>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value ?? "off"}
        onChange={(e) => {
          if (e.target.value === "off") {
            onChange(null);
          } else {
            const value =
              typeof e.target.value === "number"
                ? e.target.value
                : parseInt(e.target.value);
            onChange(value);
          }
        }}
        size="small"
        sx={{
          minWidth: 120,
          fontSize: "0.75em !important",
          "& .MuiSelect-select": { padding: "0.5em !important" },
        }}
      >
        <MenuItem value="off">{t("gestisciReminderDialog.select.off")}</MenuItem>
        <Divider />
        <MenuItem value={-1}>{t("gestisciReminderDialog.select.prima.1")}</MenuItem>
        <MenuItem value={-5}>{t("gestisciReminderDialog.select.prima.5")}</MenuItem>
        <MenuItem value={-10}>{t("gestisciReminderDialog.select.prima.10")}</MenuItem>
        <MenuItem value={-15}>{t("gestisciReminderDialog.select.prima.15")}</MenuItem>
        <MenuItem value={-30}>{t("gestisciReminderDialog.select.prima.30")}</MenuItem>
        <Divider />
        <MenuItem value={1}>{t("gestisciReminderDialog.select.dopo.1")}</MenuItem>
        <MenuItem value={5}>{t("gestisciReminderDialog.select.dopo.5")}</MenuItem>
        <MenuItem value={10}>{t("gestisciReminderDialog.select.dopo.10")}</MenuItem>
        <MenuItem value={15}>{t("gestisciReminderDialog.select.dopo.15")}</MenuItem>
        <MenuItem value={30}>{t("gestisciReminderDialog.select.dopo.30")}</MenuItem>
      </Select>
    </Box>
  );
}
